// ─── PIPELINE SLOT DEFINITIONS ──────────────────────────────────────────────
// Defines the visual stepper slots for Crea Contenuto.
// Each slot: { id, label, skippable? }
// Phase mapper: backend status string → { slotId, subLabel }
// `label` values are kept as i18n fallbacks; PipelineStepper resolves the display
// label via outputs:stepper.slots.<id>. Sub-labels are localized here (non-React
// module) via i18n.t at event time.
import i18n from '../i18n/index.js';

// ── Crea Contenuto (pipeline) — 4 slots (1 skippable) ──────────────────────
// Fact-checking is integrated into the Critic (REVIEWING stage), not a separate step.

/**
 * Build the slot array for content pipeline, marking skipped slots.
 * @param {object} opts
 * @param {boolean} opts.researchRequested - whether research phase runs
 * @returns {Array<{ id: string, label: string, skipped: boolean }>}
 */
export function buildPipelineSlots({ researchRequested }) {
  return [
    { id: 'setup',     label: 'Avvio',          skipped: false },
    { id: 'research',  label: 'Ricerca',        skipped: !researchRequested },
    { id: 'writing',   label: 'Scrittura',      skipped: false },
    { id: 'finishing',  label: 'Finalizzazione', skipped: false },
  ];
}

/**
 * Map backend pipeline status → activePhase for PipelineStepper.
 * @param {object} statusResponse - from GET /pipeline/run/{id}/status
 *   Expected shape: { status: string, iteration_count?: number }
 * @returns {{ slotId: string, subLabel: string } | null}
 */
export function mapPipelineStatus(statusResponse) {
  const { status, iteration_count } = statusResponse;
  switch (status) {
    case 'CREATED':
      return { slotId: 'setup', subLabel: i18n.t('outputs:stepper.sub.CREATED') };
    case 'RESEARCHING':
      return { slotId: 'research', subLabel: i18n.t('outputs:stepper.sub.RESEARCHING') };
    case 'DRAFTING':
      return { slotId: 'writing', subLabel: i18n.t('outputs:stepper.sub.DRAFTING') };
    case 'REVIEWING':
      return { slotId: 'writing', subLabel: i18n.t('outputs:stepper.sub.REVIEWING') };
    case 'REVISING': {
      // BE iteration_count counts the *next* iteration about to start
      // (drafting=1, first revision=2, etc.) — display as 1-based rewrite #.
      const n = iteration_count > 1 ? iteration_count - 1 : 1;
      return { slotId: 'writing', subLabel: i18n.t('outputs:stepper.sub.REVISING', { n }) };
    }
    case 'STYLING':
      return { slotId: 'finishing', subLabel: i18n.t('outputs:stepper.sub.STYLING') };
    case 'COMPLETED':
    case 'approved':
    case 'completed':
      return null; // signals completion
    case 'FAILED':
    case 'failed':
      return null; // caller checks isError separately
    default:
      // Defensive: unknown status — keep last known phase
      return null;
  }
}
